import Vue from 'vue'
import api from '@/apis/siteActivityLog'
import parser from '@/models/siteActivityLog'
import {
  RESET_STATE,
  PROCESS_API_SUCCESS,
  PROCESS_API_FAILURE,
  INIT_SITE_ACTIVITY_LOGS,
  INIT_SITE_SEARCH_ACTIVITY_LOGS,
  GET_SITE_ACTIVITY_LOGS_REQUEST,
  GET_SITE_ACTIVITY_LOGS_SUCCESS,
  GET_SITE_ACTIVITY_LOGS_FAILURE,
  GET_SITE_ACTIVITY_LOG_AUDIO_URL_REQUEST,
  GET_SITE_ACTIVITY_LOG_AUDIO_URL_SUCCESS,
  GET_SITE_ACTIVITY_LOG_AUDIO_URL_FAILURE,
  GET_DEVICE_ACTIVITY_LOGS_REQUEST,
  GET_DEVICE_ACTIVITY_LOGS_SUCCESS,
  GET_DEVICE_ACTIVITY_LOGS_FAILURE,
  GET_DEVICE_PLAYBACK_ACTIVITY_LOGS_REQUEST,
  GET_DEVICE_PLAYBACK_ACTIVITY_LOGS_SUCCESS,
  GET_DEVICE_PLAYBACK_ACTIVITY_LOGS_FAILURE,
  GET_SITE_ACTIVITY_LOGS_CATEGORY_REQUEST,
  GET_SITE_ACTIVITY_LOGS_CATEGORY_SUCCESS,
  GET_SITE_ACTIVITY_LOGS_CATEGORY_FAILURE,
  GET_SITE_ACTIVITY_LOGS_SEARCH_REQUEST,
  GET_SITE_ACTIVITY_LOGS_SEARCH_SUCCESS,
  GET_SITE_ACTIVITY_LOGS_SEARCH_FAILURE,
  GET_SITE_ACTIVITY_LOG_AUDIO_URL_V2_REQUEST,
  GET_SITE_ACTIVITY_LOG_AUDIO_URL_V2_SUCCESS,
  GET_SITE_ACTIVITY_LOG_AUDIO_URL_V2_FAILURE,
  GET_SITE_DEVICE_ACTIVITY_LOGS_SEARCH_REQUEST,
  GET_SITE_DEVICE_ACTIVITY_LOGS_SEARCH_SUCCESS,
  GET_SITE_DEVICE_ACTIVITY_LOGS_SEARCH_FAILURE,
} from '@/store/mutation-types'

/**
 * Intent Analysis 로그 분리 헬퍼
 *
 * intent 로그(item_id 32217)는 독립된 활동이 아니라 "원본 video event 에 대한 판정 결과"다.
 * 원본 이벤트와 event_id 가 같기 때문에, 그대로 흘려보내면 아래쪽 event_id 중복 제거
 * (indexByEid / byEventId) 에서 원본 이벤트와 서로를 밀어내 둘 중 하나가 사라진다.
 *
 * 그래서 리스트 파이프라인에 들어가기 전에 걷어내고, event_id 를 키로 한 맵으로 따로 보관한다.
 * 화면에서는 이 맵을 원본 이벤트 행에 뱃지로 얹어 쓴다.
 *
 * @param {Array} parsedHits - 파싱된 hit 배열
 * @returns {{ hits: Array, intents: Object }} hits = intent 를 제외한 나머지
 */
function extractIntentLogs(parsedHits) {
  const intentItemIds = Vue.actlog?.descIds?.intentAnalysis || []
  if (!intentItemIds.length) return { hits: parsedHits, intents: {} }

  const hits = []
  const intents = {}

  for (const p of parsedHits) {
    if (!intentItemIds.includes(Number(p.item_id))) {
      hits.push(p)
      continue
    }

    mergeIntentLog(intents, p)
  }

  return { hits, intents }
}

/**
 * intent 로그를 event_id 맵에 넣되, 같은 이벤트에 여러 건이 있으면 created_at 이
 * 가장 늦은 것만 남긴다.
 *
 * 한 이벤트에 intent 로그가 2건 생기는 경로가 실제로 있다:
 *   1) provider 가 판정 없이 회신 → outcome='no_verdict'.
 *      이건 종결이 아니다 — pending row 를 stamp 하지 않고 빠져서 워커 타임아웃이
 *      이 이벤트를 마감하게 둔다 (assessment.post.action.js 의 NO-VERDICT 분기).
 *   2) 이후 워커가 마감 → source='timeout', outcome='delivered'|'not_delivered'.
 *
 * 화면에 보여야 하는 건 최종 상태(2번)다. 단순 덮어쓰기로 두면 정렬 순서에 따라
 * 먼저 발생한 no_verdict 가 나중에 들어와 최종 결과를 지운다 — 검색 결과가
 * created_at 내림차순이라 배열 뒤쪽이 오히려 더 오래된 로그이기 때문이다.
 */
function mergeIntentLog (map, log) {
  const eventId = log?.event_id
  if (!eventId) return

  const prev = map[eventId]
  if (!prev) {
    map[eventId] = log
    return
  }

  const prevTime = Number(new Date(prev.created_at)) || 0
  const nextTime = Number(new Date(log.created_at)) || 0
  if (nextTime >= prevTime) map[eventId] = log
}

/**
 * AI 이벤트 필터링 헬퍼 함수
 * - AI false 이벤트(32042, 32077) 제거
 * - AI true가 있으면 base 이벤트 대신 AI true 표시
 * - AI false만 있으면 base 이벤트도 제거
 *
 * @param {Array} logs - 필터링할 로그 배열
 * @param {Object} options - 옵션 { eventIdKey: 'event_id' | 'eventId', itemIdKey: 'item_id' | 'descId' }
 * @returns {Array} - 필터링된 로그 배열
 */
function filterAIEvents(logs, options = {}) {
  const {
    eventIdKey = 'event_id',
    itemIdKey = 'item_id',
  } = options;

  // 1) event_id별로 그룹핑 (AI 필터링을 위해)
  const groupedByEventId = {};
  for (const log of logs) {
    const eid = log[eventIdKey];
    if (!eid) continue;
    if (!groupedByEventId[eid]) groupedByEventId[eid] = [];
    groupedByEventId[eid].push(log);
  }

  // 2) AI 이벤트 필터링 (alarm video: 32041/32042, disarmed video: 32054/32076/32077 처리)
  const baseAlarmVideoIds = [30011, 30305, 31223, 31085, 32033];
  const filteredLogs = [];

  for (const log of logs) {
    const eid = log[eventIdKey];
    const itemId = Number(log[itemIdKey]);

    // event_id가 없으면 그냥 추가
    if (!eid) {
      filteredLogs.push(log);
      continue;
    }

    const group = groupedByEventId[eid] || [];
    const has32041 = group.some(g => Number(g[itemIdKey]) === 32041);
    const has32042 = group.some(g => Number(g[itemIdKey]) === 32042);
    const has32076 = group.some(g => Number(g[itemIdKey]) === 32076);
    const has32077 = group.some(g => Number(g[itemIdKey]) === 32077);

    // 32042 (alarm video AI false) 무조건 제외
    if (itemId === 32042) continue;

    // 32077 (disarmed video AI false) 무조건 제외
    if (itemId === 32077) continue;

    // 기본 alarm video 이벤트 처리
    if (baseAlarmVideoIds.includes(itemId)) {
      // 32041이 있으면 기본 이벤트 제외 (32041이 대신 보임)
      if (has32041) continue;

      // 32042가 있으면 기본 이벤트도 제외 (AI false라서 아예 숨김)
      if (has32042) continue;

      // 둘 다 없으면 AI OFF 카메라 → 기본 이벤트 보여줌
      filteredLogs.push(log);
      continue;
    }

    // 32041은 그대로 추가
    if (itemId === 32041) {
      filteredLogs.push(log);
      continue;
    }

    // 32054 (disarmed video) 처리
    if (itemId === 32054) {
      // 32076이 있으면 32054 제외 (32076이 대신 보임)
      if (has32076) continue;

      // 32077이 있으면 32054도 제외 (AI false라서 아예 숨김)
      if (has32077) continue;

      // 둘 다 없으면 AI OFF 카메라 → 32054 보여줌
      filteredLogs.push(log);
      continue;
    }

    // 32076은 그대로 추가
    if (itemId === 32076) {
      filteredLogs.push(log);
      continue;
    }

    // 다른 이벤트들
    filteredLogs.push(log);
  }

  return filteredLogs;
}

let initialState = {
  // only logs in the selected site will be stored
  siteId: null,
  logs: null,
  logIds: null,
  deviceLogs: null,
  devicePlaybackActivityLogs: [],
  deviceLogIds: null,
  logsAudioUrl: null,
  logsAudioUrlV2: null,
  isDeviceLogsGetFullLogs: false,
  logsCategory: [],
  logsSearch: [],
  hasVideoEventIds: [],
  LogsThumbnailInfos: [],
  tempSearchLogs: [],
  tempSearchDeviceLogs: [],
  searchSortIds: [], 
  deviceLogsSearch: [],
  // event_id -> intent analysis log (원본 이벤트 행에 뱃지로 얹기 위한 부가정보)
  intentByEventId: {},
  status: {
    getLogs: null,
    getLogsAudioUrl: null,
    getLogsAudioUrlV2: null,
    getDeviceLogs: null,
    getLogsCategory: null,
    getLogsSearch:null,
    getDeviceLogsSearch:null,
    getLogsThumbnailInfos: null,
  },
}

// initial state
const state = Vue.util.extend({}, initialState)

// getters
const getters = {
  // site activity logs
  siteActivityLog: function (state, getters) {
    return function (logId) {
      return getters.siteActivityLogs[logId]
    }
  },
  siteActivityLogs: function (state) {
    return state.logs || {}
  },
  activityLogIntents: function (state) {
    return state.intentByEventId || {}
  },
  siteActivityLogIds: function (state) {
    return state.logIds || []
  },
  siteFirstActivityLogId: function (state, getters) {
    if (getters.siteActivityLogIds.length < 1) return ''
    return getters.siteActivityLogIds[0]
  },
  siteLastActivityLogId: function (state, getters) {
    if (getters.siteActivityLogIds.length < 1) return ''
    return getters.siteActivityLogIds[getters.siteActivityLogIds.length - 1]
  },
  statusGetSiteActivityLogs: function (state) {
    return state.status.getLogs
  },
  siteLogsAudioUrl: function (state) {
    return state.logsAudioUrl || {}
  },
  siteLogAudioUrl: function (state, getters) {
    return function (logId) {
      if (!getters.siteLogsAudioUrl[logId]) return ''
      return getters.siteLogsAudioUrl[logId]['url']
    }
  },
  statusGetLogAudioUrl: function (state) {
    return state.status.getLogsAudioUrl
  },
  siteLogsAudioUrlV2: function (state) {
    return state.logsAudioUrlV2 || {}
  },
  siteLogAudioUrlV2: function (state, getters) {
    return function (logId) {
      if (!getters.siteLogsAudioUrlV2[logId]) return ''
      return getters.siteLogsAudioUrlV2[logId]['url']
    }
  },
  statusGetLogAudioUrlV2: function (state) {
    return state.status.getLogsAudioUrlV2
  },

  // device activity logs
  deviceActivityLog: function (state, getters) {
    return function (logId) {
      return getters.deviceActivityLogs[logId]
    }
  },
  deviceActivityLogs: function (state) { 
    return state.deviceLogs || {}
  },
  deviceActivityLogIds: function (state) { 
    return state.deviceLogIds || []
  },
  deviceFirstActivityLogId: function (state, getters) {
    if (getters.deviceActivityLogIds.length < 1) return ''
    return getters.deviceActivityLogIds[0]
  },
  deviceLastActivityLogId: function (state, getters) {
    if (getters.deviceActivityLogIds.length < 1) return ''
    return getters.deviceActivityLogIds[getters.deviceActivityLogIds.length - 1]
  },
  statusGetDeviceActivityLogs: function (state) {
    return state.status.getDeviceLogs
  },
  devicePlaybackActivityLogs: function (state) { 
    return state.devicePlaybackActivityLogs || []
  },
  statusGetDevicePlaybackActivityLogs: function (state) {
    return state.status.getDevicePlaybackActivityLogs
  },
  isDeviceLogsGetFullLogs: function (state) {
    return state.isDeviceLogsGetFullLogs
  },
  siteLogsCategory: function (state) {
    return state.logsCategory
  },
  statusGetSiteActivityLogsCategory: function (state) {
    return state.status.getLogsCategory
  },
  siteActivityLogsSearch: function (state) {
    return state.logsSearch
  },
  siteActivityLogSearch: function (state, getters) {
    return function (logId) {
      return getters.siteActivityLogsSearch[logId]
    }
  },
  siteDeviceActivityLogsSearch: function (state) {
    return state.deviceLogsSearch
  },
  siteDeviceActivityLogSearch: function (state, getters) {
    return function (logId) {
      return getters.siteDeviceActivityLogsSearch[logId]
    }
  },
  hasVideoEventIds: function (state) {
    return state.hasVideoEventIds || []
  },
  LogsThumbnailInfos: function (state) {
    return state.LogsThumbnailInfos
  },
  searchSortIds: function (state) {
    return state.searchSortIds
  },
  newestSearchLogId: function (state, getters) {
    if (getters.searchSortIds.length < 1) return ''
    return getters.searchSortIds[0]
  },
  oldestSearchLogId: function (state, getters) {
    if (getters.searchSortIds.length < 1) return ''
    return getters.searchSortIds[getters.searchSortIds.length - 1]
  },
  statusGetLogsSearch: function (state) {
    return state.status.getLogsSearch
  }
}

// actions
const actions = {
  getSiteActivityLogs: function ({commit, getters}, {siteId, stime, etime, toGetNewLogs, eventTypes, siteTimezone, logPeriod, limit}) {
    commit(GET_SITE_ACTIVITY_LOGS_REQUEST, {siteId})
    // SET - targetLogId
    let targetLogId = ''
    if (toGetNewLogs) targetLogId = getters.siteLastActivityLogId
    else targetLogId = getters.siteFirstActivityLogId
    // API CALL
    return new Promise((resolve, reject) => {
      api.getSiteActivityLogs({siteId, stime, etime, targetLogId, toGetNewLogs, eventTypes, siteTimezone, logPeriod, limit}).then(
        res => {
          let data = res.body
          commit(GET_SITE_ACTIVITY_LOGS_SUCCESS, {data})
          resolve()
          commit(PROCESS_API_SUCCESS)
        },
        err => {
          commit(GET_SITE_ACTIVITY_LOGS_FAILURE)
          reject({
            status: err.status,
            statusText: err.body,
          })
          commit(PROCESS_API_FAILURE, {
            status: err.status,
            statusText: err.body,
            origin: window.location.origin,
            err: Vue.tool.parseToStringify(err),
          })
        }
      )
    })
  },
  getDeviceActivityLogs: function ({commit, getters}, {siteId, deviceId, stime, etime, toGetNewLogs, eventTypes, siteTimezone, limit}) {
    commit(GET_DEVICE_ACTIVITY_LOGS_REQUEST, { siteId })
    // SET - targetLogId
    let targetLogId = ''
    if (toGetNewLogs) targetLogId = getters.deviceLastActivityLogId
    else targetLogId = getters.deviceFirstActivityLogId

    // API CALL
    return new Promise((resolve, reject) => {
      api.getDeviceActivityLogs({siteId, stime, deviceId, targetLogId, etime, toGetNewLogs, eventTypes, siteTimezone, limit}).then(
        res => {
          let data = res.body
          commit(GET_DEVICE_ACTIVITY_LOGS_SUCCESS, {data, limit})
          resolve(data)
          commit(PROCESS_API_SUCCESS)
        },
        err => {
          commit(GET_DEVICE_ACTIVITY_LOGS_FAILURE)
          reject({
            status: err.status,
            statusText: err.body,
          })
          commit(PROCESS_API_FAILURE, {
            status: err.status,
            statusText: err.body,
            origin: window.location.origin,
            err: Vue.tool.parseToStringify(err),
          })
        }
      )
    })
  },
  getDevicePlaybackActivityLogs: function ({commit, getters}, {siteId, deviceId, stime, etime, eventTypes, siteTimezone}) {
    commit(GET_DEVICE_PLAYBACK_ACTIVITY_LOGS_REQUEST, {siteId})
    // API CALL
    return new Promise((resolve, reject) => {
      api.getDevicePlaybackActivityLogs({siteId, stime, deviceId, etime, eventTypes, siteTimezone}).then(
        res => {
          let data = res.body
          commit(GET_DEVICE_PLAYBACK_ACTIVITY_LOGS_SUCCESS, {data})
          resolve()
          commit(PROCESS_API_SUCCESS)
        },
        err => {
          commit(GET_DEVICE_PLAYBACK_ACTIVITY_LOGS_FAILURE)
          reject({
            status: err.status,
            statusText: err.body,
          })
          commit(PROCESS_API_FAILURE, {
            status: err.status,
            statusText: err.body,
            origin: window.location.origin,
            err: Vue.tool.parseToStringify(err),
          })
        }
      )
    })
  },
  getSiteActivityLogAudioUrl: function ({commit, getters}, {siteId, logId}) {

    // FILTER - 한번 요청한 audio activity log라면 호출하지 말고 있는걸 써라!
    if (getters.siteLogsAudioUrl.hasOwnProperty(logId)) {
      const maxValidTime = 1000*60*50 // 50분
      let url = getters.siteLogsAudioUrl[logId]
      if (url) {
        // URL을 받은지 50분이 지났으면 다시 받아온다 * 21/11/09 현재시점 URL 유효기간이 1시간임.
        if (url.created.getTime() > new Date().getTime() - maxValidTime) return
      }
    }

    commit(GET_SITE_ACTIVITY_LOG_AUDIO_URL_REQUEST, {siteId})
    // API CALL
    return new Promise((resolve, reject) => {
      api.getSiteActivityLogAudioUrl({siteId, logId}).then(
        res => {
          let data = res.body
          commit(GET_SITE_ACTIVITY_LOG_AUDIO_URL_SUCCESS, {data, logId},)
          resolve(data)
          commit(PROCESS_API_SUCCESS)
        },
        err => {
          commit(GET_SITE_ACTIVITY_LOG_AUDIO_URL_FAILURE)
          reject({
            status: err.status,
            statusText: err.body,
          })
          commit(PROCESS_API_FAILURE, {
            status: err.status,
            statusText: err.body,
            origin: window.location.origin,
            err: Vue.tool.parseToStringify(err),
          })
        }
      )
    })
  },
  getSiteActivityLogAudioUrlV2: function ({commit, getters}, {siteId, logId}) {

    // FILTER - 한번 요청한 audio activity log라면 호출하지 말고 있는걸 써라!
    if (getters.siteLogsAudioUrlV2.hasOwnProperty(logId)) {
      const maxValidTime = 1000*60*50 // 50분
      let url = getters.siteLogsAudioUrlV2[logId]
      if (url) {
        // URL을 받은지 50분이 지났으면 다시 받아온다 * 21/11/09 현재시점 URL 유효기간이 1시간임.
        if (url.created.getTime() > new Date().getTime() - maxValidTime) return
      }
    }

    commit(GET_SITE_ACTIVITY_LOG_AUDIO_URL_V2_REQUEST, {siteId})
    // API CALL
    return new Promise((resolve, reject) => {
      api.getSiteActivityLogAudioUrlV2({siteId, logId}).then(
        res => {
          let data = res.body
          commit(GET_SITE_ACTIVITY_LOG_AUDIO_URL_V2_SUCCESS, {data, logId},)
          resolve(data)
          commit(PROCESS_API_SUCCESS)
        },
        err => {
          commit(GET_SITE_ACTIVITY_LOG_AUDIO_URL_V2_FAILURE)
          reject({
            status: err.status,
            statusText: err.body,
          })
          commit(PROCESS_API_FAILURE, {
            status: err.status,
            statusText: err.body,
            origin: window.location.origin,
            err: Vue.tool.parseToStringify(err),
          })
        }
      )
    })
  },
  getSiteActivityLogsCategory: function ({commit, getters}, {siteId}) {
    commit(GET_SITE_ACTIVITY_LOGS_CATEGORY_REQUEST, {siteId})
    // API CALL
    return new Promise((resolve, reject) => {
      api.getSiteActivityLogsCategory({siteId}).then(
        res => {
          let data = res.body
          commit(GET_SITE_ACTIVITY_LOGS_CATEGORY_SUCCESS, {data},)
          resolve(data)
          commit(PROCESS_API_SUCCESS)
        },
        err => {
          commit(GET_SITE_ACTIVITY_LOGS_CATEGORY_FAILURE)
          reject({
            status: err.status,
            statusText: err.body,
          })
          commit(PROCESS_API_FAILURE, {
            status: err.status,
            statusText: err.body,
            origin: window.location.origin,
            err: Vue.tool.parseToStringify(err),
          })
        }
      )
    })
  },
  getSiteActivityLogsSearch: function ({commit, getters, dispatch}, {siteId, type, siteTimezone, eventTypes, logPeriod, etime, stime, ai, device_id, partition_numbers}) {
    commit(GET_SITE_ACTIVITY_LOGS_SEARCH_REQUEST, {siteId})

    let targetLogId = ''
    if (type === "newer") targetLogId = getters.oldestSearchLogId
    else if (type === "older") targetLogId = getters.newestSearchLogId

    // API CALL
    return new Promise((resolve, reject) => {
      api.getSiteActivityLogsSearch({siteId, type, eventTypes, logPeriod, targetLogId, siteTimezone, etime, stime, ai, device_id, partition_numbers}).then(
        async (res) => {
          let data = res.body
          commit(GET_SITE_ACTIVITY_LOGS_SEARCH_SUCCESS, {siteId, data, siteTimezone, type},)
          resolve(data)
          commit(PROCESS_API_SUCCESS)
        },
        err => {
          commit(GET_SITE_ACTIVITY_LOGS_SEARCH_FAILURE)
          reject({
            status: err.status,
            statusText: err.body,
          })
          commit(PROCESS_API_FAILURE, {
            status: err.status,
            statusText: err.body,
            origin: window.location.origin,
            err: Vue.tool.parseToStringify(err),
          })
        }
      )
    })
  },
  getSiteDeviceActivityLogsSearch: function ({commit, getters, dispatch, state}, {siteId, deviceId, type, siteTimezone, eventTypes, logPeriod, etime}) {
    commit(GET_SITE_DEVICE_ACTIVITY_LOGS_SEARCH_REQUEST, {siteId})

    let targetLogId = ''
    if (type === "newer") targetLogId = getters.oldestSearchLogId
    else if (type === "older") targetLogId = getters.newestSearchLogId

    // API CALL
    return new Promise((resolve, reject) => {
      api.getSiteDeviceActivityLogsSearch({siteId, deviceId, type, eventTypes, logPeriod, targetLogId, siteTimezone, etime }).then(
        async (res) => {
          let data = res.body
          commit(GET_SITE_DEVICE_ACTIVITY_LOGS_SEARCH_SUCCESS, {siteId, data, type, siteTimezone},)
          resolve(data)
          commit(PROCESS_API_SUCCESS)
        },
        err => {
          commit(GET_SITE_DEVICE_ACTIVITY_LOGS_SEARCH_FAILURE)
          reject({
            status: err.status,
            statusText: err.body,
          })
          commit(PROCESS_API_FAILURE, {
            status: err.status,
            statusText: err.body,
            origin: window.location.origin,
            err: Vue.tool.parseToStringify(err),
          })
        }
      )
    })
  }
}

// mutations
const mutations = {
  [RESET_STATE]: function (state) {
    for (let f in state) {
      Vue.set(state, f, initialState[f])
    }
  },
  [INIT_SITE_ACTIVITY_LOGS]: function (state) {
    for (let f in state) {
      Vue.set(state, f, initialState[f])
    }
  },
  [INIT_SITE_SEARCH_ACTIVITY_LOGS]: function (state) {
    for (let f in state) {
      Vue.set(state, f, initialState[f])
    }
  },
  [GET_SITE_ACTIVITY_LOGS_REQUEST]: function (state, {siteId}) {
    // RESET_STATE - site or dateKey changed
    if (state.siteId && state.siteId !== siteId) {
      for (let f in state) {
        Vue.set(state, f, initialState[f])
      }
    }
    state.siteId = siteId
    state.status.getLogs = "requested"
  },
  [GET_SITE_ACTIVITY_LOGS_SUCCESS]: function (state, {data}) {
    // CHECK - data is array
    if (!(data instanceof Array)) return

    // INIT
    let logs = {}
    let logIds = []

    // 정렬 오름차순
    data.sort((a,b) => new Date(a.created) - new Date(b.created))

    // filter duplicated logs
    let noDuplicatedArr = []
    let duplicatedArr = []

    // 우선순위를 가질 desc_id 목록 (여러 개 추가 가능)
    const priorityDescIds = [32041, 32076]; // 예: [32041, 32042, 32043]

    // 1. devide duplicated & not duplicated logs
    for (let i = 0; i < data.length; i++) {
      const log = data[i];
      if (!noDuplicatedArr.some(e => e.event_id === log.event_id && e.event_id)) {
        noDuplicatedArr.push(log)
      }
      else duplicatedArr.push(log)
    }

    // 2. do something.. from duplicated logs 
    for (let j = 0; j < duplicatedArr.length; j++) {
      const duplicated = duplicatedArr[j];

      // 우선순위 desc_id 목록에 포함된 경우 교체
      if (priorityDescIds.includes(duplicated.desc_id)) {
        for (let jj = 0; jj < noDuplicatedArr.length; jj++) {
          const noDuplicated = noDuplicatedArr[jj];
          if (noDuplicated.event_id === duplicated.event_id) {
            // 기존 항목이 우선순위 목록에 없으면 교체
            if (!priorityDescIds.includes(noDuplicated.desc_id)) {
              noDuplicatedArr[jj] = duplicated
            }
          }
        }
      }

    }

    // 3. push in data
    data = noDuplicatedArr

    // PARSE
    for (let k = 0; k < data.length; k++) {
      const logData = data[k];
      const logInfo = parser.parseActivityLogData(logData)
      const logId = logInfo.logId
      if (logId) {
        logs[logId] = logInfo
        logIds.push(logId)
      }
    }

    // MERGE - logIds with existing logIds
    if (state.logIds instanceof Array) {
      state.logIds.map(logId => {
        if (logIds.indexOf(logId) < 0) logIds.push(logId)
      })
    }

    // SORT - without affecting `state.logIds`
    logIds.sort((a,b) => a-b)

    // SET - state.logIds
    Vue.set(state, 'logIds', logIds)

    // COMBINE existing logs with newly retrieved logs
    // CASE 1,2,3,4 - To prlog duplicated log retrieval
    if (!(state.logs instanceof Object)) state.logs = {}
    Object.assign(state.logs, logs)
    state.status.getLogs = "successful"
  },
  [GET_SITE_ACTIVITY_LOGS_FAILURE]: function (state) {
    state.status.getLogs = "failed"
  },
  [GET_SITE_ACTIVITY_LOG_AUDIO_URL_REQUEST]: function (state) {
    state.status.getLogsAudioUrl = "requested"
  },
  [GET_SITE_ACTIVITY_LOG_AUDIO_URL_SUCCESS]: function (state, {data, logId},) {
    // CHECK - logsAudioUrl is object  & is get url
    if (!data.signed_url) return
    if (!(state.logsAudioUrl instanceof Object)) state.logsAudioUrl = {}
    if (!(state.logsAudioUrl[logId] instanceof Object)) state.logsAudioUrl[logId] = {}

    // ACTION - Get Url
    state.logsAudioUrl[logId]['url'] = data.signed_url
    state.logsAudioUrl[logId]['created'] = new Date()
    state.status.getLogsAudioUrl = "successful"
  },
  [GET_SITE_ACTIVITY_LOG_AUDIO_URL_FAILURE]: function (state) {
    state.status.getLogsAudioUrl = "failed"
  },
  [GET_SITE_ACTIVITY_LOG_AUDIO_URL_V2_REQUEST]: function (state) {
    state.status.getLogsAudioUrlV2 = "requested"
  },
  [GET_SITE_ACTIVITY_LOG_AUDIO_URL_V2_SUCCESS]: function (state, {data, logId},) {
    // CHECK - logsAudioUrl is object  & is get url
    if (!data.signed_url) return
    if (!(state.logsAudioUrlV2 instanceof Object)) state.logsAudioUrlV2 = {}
    if (!(state.logsAudioUrlV2[logId] instanceof Object)) state.logsAudioUrlV2[logId] = {}
    
    // ACTION - Get Url
    state.logsAudioUrlV2[logId]['url'] = data.signed_url
    state.logsAudioUrlV2[logId]['created'] = new Date()
    state.status.getLogsAudioUrlV2 = "successful"
  },
  [GET_SITE_ACTIVITY_LOG_AUDIO_URL_V2_FAILURE]: function (state) {
    state.status.getLogsAudioUrlV2 = "failed"
  },
  [GET_DEVICE_ACTIVITY_LOGS_REQUEST]: function (state, { siteId }) {
    if (state.siteId && state.siteId !== siteId) {
      for (let f in state) {
        Vue.set(state, f, initialState[f])
      }
    }
    state.siteId = siteId
    state.status.getDeviceLogs = "requested"
  },
  [GET_DEVICE_ACTIVITY_LOGS_SUCCESS]: function (state, {data, limit}) {
    // CHECK - data is array
    if (!(data instanceof Array)) return
    if (!limit) limit = 50 // default limit 50

    if (limit > data.length) state.isDeviceLogsGetFullLogs = false
    else state.isDeviceLogsGetFullLogs = true

    // INIT
    let deviceLogs = {}
    let deviceLogIds = []
    // 정렬 오름차순
    data.sort((a, b) => new Date(a.created) - new Date(b.created))
    
    // filter duplicated logs
    let noDuplicatedArr = []
    let duplicatedArr = []

    // 1. divide duplicated & not duplicated logs
    for (let i = 0; i < data.length; i++) {
      const log = data[i];
      if (!noDuplicatedArr.some(e => e.event_id === log.event_id && e.event_id)) {
        noDuplicatedArr.push(log)
      }
      else duplicatedArr.push(log)
    }
    
    // 2. do something.. from duplicated logs
    for (let j = 0; j < duplicatedArr.length; j++) {
      const duplicated = duplicatedArr[j];

      // Only show AI event log
      if (duplicated.desc_id == 32041) {
        for (let jj = 0; jj < noDuplicatedArr.length; jj++) {
          const noDuplicated = noDuplicatedArr[jj];
          if (noDuplicated.event_id === duplicated.event_id) {
            noDuplicatedArr[jj] = duplicated
          }
        }
      }
    }
    // 3. push in data
    data = noDuplicatedArr

    // PARSE
    for (let k = 0; k < data.length; k++) {
      const logData = data[k];
      const logInfo = parser.parseActivityLogData(logData)
      const logId = logInfo.logId
      if (logId) {
        deviceLogs[logId] = logInfo
        deviceLogIds.push(logId)
      }
    }
    // MERGE - logIds with existing logIds
    if (state.deviceLogIds instanceof Array) {
      state.deviceLogIds.map(logId => {
        if (deviceLogIds.indexOf(logId) < 0) deviceLogIds.push(logId)
      })
    }

    // SORT - without affecting `state.deviceLogIds`
    deviceLogIds.sort((a, b) => a - b)

    // SET - state.deviceLogIds
    Vue.set(state, 'deviceLogIds', deviceLogIds)

    // COMBINE existing logs with newly retrieved logs
    // CASE 1,2,3,4 - To prlog duplicated log retrieval
    if (!(state.deviceLogs instanceof Object)) state.deviceLogs = {}
    Object.assign(state.deviceLogs, deviceLogs)
    state.status.getDeviceLogs = "successful"
  },
  [GET_DEVICE_ACTIVITY_LOGS_FAILURE]: function (state) {
    state.isDeviceLogsGetFullLogs = false
    state.status.getDeviceLogs = "failed"
  },
  [GET_DEVICE_PLAYBACK_ACTIVITY_LOGS_REQUEST]: function (state, { siteId }) {
    state.devicePlaybackActivityLogs = []

    if (state.siteId && state.siteId !== siteId) {
      for (let f in state) {
        Vue.set(state, f, initialState[f])
      }
    }
    state.siteId = siteId
    state.status.getDevicePlaybackActivityLogs = "requested"
  },
  [GET_DEVICE_PLAYBACK_ACTIVITY_LOGS_SUCCESS]: function (state, { data, siteTimezone }) {
    const hits = data?.logs?.hits?.hits || []
    const thumbnailInfos = data?.thumbnail_urls?.thumbnail_infos || {}
    const ids = Vue.actlog.deviceDescIds

    // 1) 먼저 모든 로그를 파싱
    const parsedLogs = []
    hits.forEach(hit => {
      const source = hit._source
      if (!source) return

      const logId = hit._id
      const eventId = source.event_id
      const descId = source.item_id
      // sort[0]이 밀리초 타임스탬프이므로 초로 변환
      const timestamp = hit.sort && hit.sort[0] ? Math.floor(hit.sort[0] / 1000) : Math.floor(new Date(source.source_event_time).getTime() / 1000)

      // type 판단
      let type
      if (ids.alarmVideo.includes(descId)) type = 'alarm'
      else if (ids.disarmedVideo.includes(descId)) type = 'activity'
      else type = 'event'

      // aiType 판단
      let aiType = 3 // 기본값
      const summaryEntityIds = source.summary_entity_ids
      if (summaryEntityIds) {
        const staticEntities = summaryEntityIds.static || []
        const motionEntities = summaryEntityIds.motion || []
        const allEntities = [...staticEntities, ...motionEntities]

        const hasPerson = allEntities.includes('person')
        const hasVehicle = allEntities.includes('vehicle')

        if (hasPerson && hasVehicle) {
          aiType = 0 // person과 vehicle 둘 다 있을 때
        } else if (hasPerson) {
          aiType = 1 // person만 있을 때
        } else if (hasVehicle) {
          aiType = 2 // vehicle만 있을 때
        }
        // 아무것도 없으면 aiType은 3 (기본값)
      }

      const parsedLog = {
        logId: String(logId),
        eventId: String(eventId),
        descId: descId,
        type: type,
        timestamp: timestamp,
        aiType: aiType,
        visible: true
      }

      // 썸네일 정보 추가
      if (thumbnailInfos[eventId]) {
        parsedLog.thumbnail = thumbnailInfos[eventId].thumbnail_url
      }

      parsedLogs.push(parsedLog)
    })

    // 2) AI 이벤트 필터링 (alarm video: 32041/32042, disarmed video: 32054/32076/32077 처리)
    const logs = filterAIEvents(parsedLogs, { eventIdKey: 'eventId', itemIdKey: 'descId' })

    // 3) 시간순 정렬 (오름차순)
    logs.sort((a, b) => a.timestamp - b.timestamp)

    state.devicePlaybackActivityLogs = logs
    state.status.getDevicePlaybackActivityLogs = "successful"
  },
  [GET_DEVICE_PLAYBACK_ACTIVITY_LOGS_FAILURE]: function (state) {
    state.devicePlaybackActivityLogs = []
    state.status.getDevicePlaybackActivityLogs = "failed"
  },
  [GET_SITE_ACTIVITY_LOGS_CATEGORY_REQUEST]: function (state) {
    state.status.getLogsCategory = "requested"
  },
  [GET_SITE_ACTIVITY_LOGS_CATEGORY_SUCCESS]: function (state, {data}) {
    state.logsCategory = data
    state.status.getLogsCategory = "successful"
  },
  [GET_SITE_ACTIVITY_LOGS_CATEGORY_FAILURE]: function (state) {
    state.status.getLogsCategory = "failed"
  },
  [GET_SITE_ACTIVITY_LOGS_SEARCH_REQUEST]: function (state) {
    state.status.getLogsSearch = "requested"
  },
  [GET_SITE_ACTIVITY_LOGS_SEARCH_SUCCESS]: async function (state, { data, siteTimezone, type }) {
    // 1) 초기화
    state.tempSearchLogs = []
    state.hasVideoEventIds = []
    let hits = data?.logs?.hits?.hits || []

    // 2) 파싱 + 썸네일 병합
    const parsedHits = [];
    const thumbnailInfos = data?.thumbnail_urls?.thumbnail_infos || {};
    const videoaiThumbnailInfos = data?.thumbnail_urls?.videoai_thumbnail_infos || {};

    for (let i = 0; i < hits.length; i++) {
      const p = parser.parseActivityLogSearchData(hits[i], siteTimezone);
      if (!p) continue;

      // 썸네일 정보 즉시 병합
      const eventId = p.event_id;
      if (thumbnailInfos[eventId]) {
        p.thumbnail = thumbnailInfos[eventId].thumbnail_url;
      }

      if (videoaiThumbnailInfos[eventId]?.detection_summary) {
        p.aiThumbnailData = JSON.parse(videoaiThumbnailInfos[eventId].detection_summary);
      }
      parsedHits.push(p);
    }

    // 2-1) intent analysis 로그는 행이 아니라 부가정보 — 중복 제거에 닿기 전에 걷어낸다
    const { hits: nonIntentHits, intents } = extractIntentLogs(parsedHits);
    const mergedIntents = { ...(state.intentByEventId || {}) };
    for (const key of Object.keys(intents)) mergeIntentLog(mergedIntents, intents[key]);
    Vue.set(state, 'intentByEventId', mergedIntents);

    // 3) AI 이벤트 필터링 (alarm video: 32041/32042, disarmed video: 32054/32076/32077 처리)
    const aiFilteredHits = filterAIEvents(nonIntentHits, { eventIdKey: 'event_id', itemIdKey: 'item_id' });

    // 4) 우선순위를 가질 item_id 목록 (event_id 중복 제거용)
    const priorityItemIds = [32041, 32076];
    const indexByEid = new Map();

    for (const p of aiFilteredHits) {
      const eid = p.event_id;

      if (!eid) {
        indexByEid.set(Symbol(), p)
        continue;
      }

      const prev = indexByEid.get((eid));
      if (!prev) {
        indexByEid.set(eid, p);
      } else {
        const prevIsPriority = priorityItemIds.includes(Number(prev.item_id));
        const currIsPriority = priorityItemIds.includes(Number(p.item_id));
        // 현재 항목이 우선순위 목록에 있고, 기존 항목이 우선순위 목록에 없으면 교체
        if (currIsPriority && !prevIsPriority) {
          indexByEid.set(eid, p);
        }
      }
    }
    const finalArr = Array.from(indexByEid.values());

    // 6) 최종 배열 사용
    const displayArr = finalArr;

    // 7) hasVideoEventIds / logsSearchArr / incomingSorts 수집
    const logsSearchArr = [];
    const hasVideoEventIds = [];
    const hasVideoDescIds = [30011, 30305, 30021, 30022, 31223, 31085, 32033, 32037, 32041, 32054, 32076, 31225, 31226];

    // 커서 전진용: 필터링 전 전체 parsedHits의 sort값 수집 (AI 필터로 걸러져도 커서는 전진해야 함)
    const incomingSorts = parsedHits.filter(p => p.sort != null).map(p => p.sort);

    for (const ph of displayArr) {
      if (hasVideoDescIds.includes(ph.item_id)) {
        hasVideoEventIds.push(ph.event_id);
      }
      logsSearchArr.push(ph);
    }

    // 5) 기존 MERGE/SORT/SET 블록 전부 제거하고 아래 한 줄로 대체
    const existingEventIdMap = new Map();
    (state.logsSearch || []).forEach((log, index) => {
      if (log.event_id) {
        existingEventIdMap.set(String(log.event_id), { log, index });
      }
    });
    
    const finalNewLogs = [];
    const logsToRemove = new Set();
    
    for (const newLog of logsSearchArr) {
      if (!newLog.event_id) {
        finalNewLogs.push(newLog);
        continue;
      }
      
      const key = String(newLog.event_id);
      const existing = existingEventIdMap.get(key);
      
      if (!existing) {
        finalNewLogs.push(newLog);
      } else {
        const existingIsPriority = priorityItemIds.includes(Number(existing.log.item_id));
        const newIsPriority = priorityItemIds.includes(Number(newLog.item_id));
        
        if (newIsPriority && !existingIsPriority) {
          logsToRemove.add(existing.index);
          finalNewLogs.push(newLog);
        }
      }
    }
    
    const filteredExisting = (state.logsSearch || []).filter((_, idx) => !logsToRemove.has(idx));
    
    state.logsSearch = [...filteredExisting, ...finalNewLogs];

    // 6) 정렬 키 전역 관리
    Vue.set(
      state,
      'searchSortIds',
      [...(state.searchSortIds || []), ...incomingSorts]
        // 딥-중복 제거
        .filter((t, i, arr) =>
          arr.findIndex(u => u.length === t.length && u.every((v, idx) => v === t[idx])) === i
        )
        // 오름차순 정렬: [source_event_time, created_at, _id]
        .sort((a, b) => {
          for (let i = 0; i < Math.max(a.length, b.length); i++) {
            const av = a[i], bv = b[i];
            if (av === bv) continue;
            if (typeof av === 'number' && typeof bv === 'number') return av - bv; // 숫자 ASC
            // return String(av).localeCompare(String(bv)); // 문자열 ASC
          }
          return 0;
        })
    );

    state.tempSearchLogs = logsSearchArr
    state.hasVideoEventIds = hasVideoEventIds
    state.status.getLogsSearch = "successful"
  },
  [GET_SITE_ACTIVITY_LOGS_SEARCH_FAILURE]: function (state) {
    state.status.getLogsSearch = "failed"
  },
  [GET_SITE_DEVICE_ACTIVITY_LOGS_SEARCH_REQUEST]: function (state) {
    state.status.getDeviceLogsSearch = "requested"
  },
  [GET_SITE_DEVICE_ACTIVITY_LOGS_SEARCH_SUCCESS]: async function (state, { data, type, siteTimezone }) {
    // 1) 초기화
    state.tempSearchDeviceLogs = []
    state.hasVideoEventIds = []

    let hits = []
    if (type === "newer") hits = data?.logs?.hits?.hits || []
    else hits = data?.logs?.hits?.hits || []

    // 2) 파싱 + 썸네일 병합
    const parsedHits = [];
    const thumbnailInfos = data?.thumbnail_urls?.thumbnail_infos || {};
    const videoaiThumbnailInfos = data?.thumbnail_urls?.videoai_thumbnail_infos || {};

    for (let i = 0; i < hits.length; i++) {
      const p = parser.parseActivityLogSearchData(hits[i], siteTimezone);
      if (!p) continue;

      // 썸네일 정보 즉시 병합
      const eventId = p.event_id;
      if (thumbnailInfos[eventId]) {
        p.thumbnail = thumbnailInfos[eventId].thumbnail_url;
      }

      if (videoaiThumbnailInfos[eventId]?.detection_summary) {
        p.aiThumbnailData = JSON.parse(videoaiThumbnailInfos[eventId].detection_summary);
      }

      parsedHits.push(p);
    }

    // 2-1) intent analysis 로그는 행이 아니라 부가정보 — 중복 제거에 닿기 전에 걷어낸다
    const { hits: nonIntentHits, intents } = extractIntentLogs(parsedHits);
    const mergedIntents = { ...(state.intentByEventId || {}) };
    for (const key of Object.keys(intents)) mergeIntentLog(mergedIntents, intents[key]);
    Vue.set(state, 'intentByEventId', mergedIntents);

    // 3) AI 이벤트 필터링 (alarm video: 32041/32042, disarmed video: 32054/32076/32077 처리)
    const aiFilteredHits = filterAIEvents(nonIntentHits, { eventIdKey: 'event_id', itemIdKey: 'item_id' });

    // 4) event_id 기준 중복 제거 + AI(32041, 32076) 우선 교체
    const priorityItemIds = [32041, 32076];
    const byEventId = new Map();
    for (const p of aiFilteredHits) {
      const eid = p.event_id;
      if (!eid) {
        byEventId.set(Symbol(), p)
        continue;
      }

      const prev = byEventId.get(eid);
      if (!prev) {
        byEventId.set(eid, p);
      } else {
        const prevIsPriority = priorityItemIds.includes(Number(prev.item_id));
        const currIsPriority = priorityItemIds.includes(Number(p.item_id));

        if (currIsPriority && !prevIsPriority) {
          byEventId.set(eid, p);
        }
      }
    }

    // 6) 최종 배열 materialize (삽입 순서 유지)
    const finalArr = Array.from(byEventId.values());

    // 7) 최종 배열 사용
    const displayArr = finalArr;

    // 8) hasVideoEventIds / logsSearchArr / incomingSorts 수집
    const logsSearchArr = [];
    const hasVideoEventIds = [];
    const incomingSorts = [];
    const hasVideoDescIds = [30011, 30305, 30021, 30022, 31223, 31085, 32033, 32037, 32041, 32054, 32076, 31225, 31226];

    for (const ph of displayArr) {
      if (hasVideoDescIds.includes(ph.item_id)) {
        hasVideoEventIds.push(ph.event_id);
      }
      logsSearchArr.push(ph);
      if (ph.sort != null) incomingSorts.push(ph.sort);
    }

    const existingEventIdMap = new Map();
    (state.deviceLogsSearch || []).forEach((log, index) => {
      if (log.event_id) {
        existingEventIdMap.set(String(log.event_id), { log, index });
      }
    });

    const finalNewLogs = [];
    const logsToRemove = new Set();

    for (const newLog of logsSearchArr) {
      if (!newLog.event_id) {
        // event_id가 없는 로그는 그냥 새로 추가
        finalNewLogs.push(newLog);
        continue;
      }

      const key = String(newLog.event_id);
      const existing = existingEventIdMap.get(key);

      if (!existing) {
        // 기존에 없는 event_id면 추가
        finalNewLogs.push(newLog);
      } else {
        const existingIsPriority = priorityItemIds.includes(Number(existing.log.item_id));
        const newIsPriority = priorityItemIds.includes(Number(newLog.item_id));

        // 새 로그가 우선순위이고, 기존 로그가 우선순위가 아니면 교체
        if (newIsPriority && !existingIsPriority) {
          logsToRemove.add(existing.index);
          finalNewLogs.push(newLog);
        }
        // 둘 다 우선순위거나 둘 다 일반이면 기존 유지
      }
    }

    // 기존 데이터에서도 AI false 이벤트 제거
    const filteredExisting = (state.deviceLogsSearch || []).filter((log, idx) => {
      // logsToRemove에 있는 항목 제거
      if (logsToRemove.has(idx)) return false;

      // AI false 이벤트 제거 (32042, 32077)
      const itemId = Number(log.item_id);
      if (itemId === 32042 || itemId === 32077) return false;

      return true;
    });

    // 최종 device용 검색 목록
    state.deviceLogsSearch = [...filteredExisting, ...finalNewLogs];

    // 기존 MERGE/SORT/SET 블록 전부 제거하고 아래 한 줄로 대체
    Vue.set(
      state,
      'searchSortIds',
      [...(state.searchSortIds || []), ...incomingSorts]
        // 딥-중복 제거
        .filter((t, i, arr) =>
          arr.findIndex(u => u.length === t.length && u.every((v, idx) => v === t[idx])) === i
        )
        // 오름차순 정렬: [source_event_time, created_at, _id]
        .sort((a, b) => {
          for (let i = 0; i < Math.max(a.length, b.length); i++) {
            const av = a[i], bv = b[i];
            if (av === bv) continue;
            if (typeof av === 'number' && typeof bv === 'number') return av - bv; // 숫자 ASC
            // return String(av).localeCompare(String(bv)); // 문자열 ASC
          }
          return 0;
        })
    );

    state.tempSearchDeviceLogs = logsSearchArr
    state.hasVideoEventIds = hasVideoEventIds
    state.status.getDeviceLogsSearch = "successful"
  },
  [GET_SITE_DEVICE_ACTIVITY_LOGS_SEARCH_FAILURE]: function (state) {
    state.status.getDeviceLogsSearch = "failed"
  }
}

export default {
  state,
  getters,
  actions,
  mutations
}